import React from 'react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useDeleteClient, type DeleteClientProps } from '@client/hooks/use-delete-client';

import { possessive, ActionStatus } from '@shared';
import { selectClientStatus } from '@client/model';

export function DeleteClientDialog({ open, onClose, client }: DeleteClientProps) {
  const {
    clientName,
    showDeleteModal,
    isLoading,
    showArchivedModal,
    showCCPADeleteModal,
    handleDeleteClient,
    handleArchiveClient,
    setCCPADeletionModalState,
  } = useDeleteClient({ open, onClose, client });

  return (
    <AlertDialog
      open={open}
      onOpenChange={onClose}
    >
      <AlertDialogContent className="w-full max-w-xl">
        {isLoading ? (
          <LoadingContent />
        ) : (
          <>
            {showDeleteModal && (
              <DeleteClientContent
                clientName={clientName}
                handleDeleteClient={handleDeleteClient}
              />
            )}
            {showArchivedModal && (
              <ArchiveClientContent
                clientName={clientName}
                handleArchiveClient={handleArchiveClient}
                handleProceedWithDeletion={() => setCCPADeletionModalState(true)}
              />
            )}
            {showCCPADeleteModal && (
              <DeleteClientCCPAContent
                clientName={clientName}
                handleDeleteClient={handleDeleteClient}
              />
            )}
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

function LoadingContent() {
  return (
    <div className="text-primary-dark py-16">
      <Loader2 className="mx-auto size-6 animate-spin" />
    </div>
  );
}

function DeleteClientContent({
  clientName,
  handleDeleteClient,
}: {
  clientName: string;
  handleDeleteClient: () => void;
}) {
  const status = useSelector(selectClientStatus);
  const isLoading = status === ActionStatus.deleting;

  return (
    <>
      <AlertDialogHeader className="text-start">
        <AlertDialogTitle>Delete Client?</AlertDialogTitle>
        <AlertDialogDescription>
          This will permanently delete all of {possessive(clientName)} data, except for a legally required
          log of their basic contact information and consent history.
          <br />
          <br />
          This action cannot be undone.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter className="mt-4">
        <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
        <Button
          disabled={isLoading}
          variant="destructive"
          onClick={handleDeleteClient}
        >
          {isLoading ? (
            <>
              Deleting
              <Loader2 className="size-4 animate-spin" />
            </>
          ) : (
            'Delete Client'
          )}
        </Button>
      </AlertDialogFooter>
    </>
  );
}

function ArchiveClientContent({
  clientName,
  handleArchiveClient,
  handleProceedWithDeletion,
}: {
  clientName: string;
  handleArchiveClient: () => void;
  handleProceedWithDeletion: () => void;
}) {
  const status = useSelector(selectClientStatus);
  const isLoading = status === ActionStatus.archiving;

  return (
    <>
      <AlertDialogHeader className="text-start">
        <AlertDialogTitle>Deletion Not Permitted</AlertDialogTitle>
        <AlertDialogDescription>
          Because {clientName} has a submitted insurance application, their data is subject to a
          7-year regulatory retention period and cannot be deleted.
          <br />
          <br />
          You can <strong>Archive Client</strong> to remove them from your active lists while
          maintaining their records for compliance.
          <br />
          <br />
          <strong>Legal Exception:</strong> If you are processing a formal request from the client
          under a privacy law like CCPA, you may proceed with deletion.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter className="mt-4">
        <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
        <Button
          variant="outline"
          disabled={isLoading}
          onClick={handleArchiveClient}
          className="mt-2 sm:mt-0"
        >
          {isLoading ? (
            <>
              Archiving
              <Loader2 className="size-4 animate-spin" />
            </>
          ) : (
            'Archive Client'
          )}
        </Button>
        <Button
          disabled={isLoading}
          onClick={handleProceedWithDeletion}
        >
          Proceed with Deletion
        </Button>
      </AlertDialogFooter>
    </>
  );
}

function DeleteClientCCPAContent({
  clientName,
  handleDeleteClient,
}: {
  clientName: string;
  handleDeleteClient: () => void;
}) {
  const status = useSelector(selectClientStatus);
  const isLoading = status === ActionStatus.deleting;

  const [isConsent, setIsConsent] = React.useState(false);

  return (
    <>
      <AlertDialogHeader className="text-start">
        <AlertDialogTitle>Final Confirmation: Delete Client Data</AlertDialogTitle>
        <AlertDialogDescription>
          You are about to permanently delete all of {clientName} personal information and
          application data.
          <br />
          <br />
          <strong>Third-Party Notice:</strong> This action only deletes data from the Waterlily
          platform. Data already submitted to insurance carriers or other partners is not affected.
          <br />
          <br />
          To proceed, you must certify the following:
          <br />
          <br />
          <div className="flex gap-2">
            <Checkbox
              id="ccpa-certification"
              className="translate-y-0.5"
              checked={isConsent}
              onCheckedChange={checked => setIsConsent(Boolean(checked))}
            />
            <label
              htmlFor="ccpa-certification"
              className="text-primary-dark pointer-cursor select-none font-semibold"
            >
              I certify that this deletion is being requested to comply with a formal request from
              the end client under an applicable privacy law (e.g., CCPA).
            </label>
          </div>
          <br />
          <br />
          This action is irreversible.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter className="mt-4">
        <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
        <Button
          disabled={isLoading || !isConsent}
          variant="destructive"
          onClick={handleDeleteClient}
        >
          {isLoading ? (
            <>
              Deleting
              <Loader2 className="size-4 animate-spin" />
            </>
          ) : (
            'Delete Client'
          )}
        </Button>
      </AlertDialogFooter>
    </>
  );
}
